import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Briefcase } from 'lucide-react';

function BusinessName() {
  const [businessName, setBusinessName] = useState('');
  const [driver, setDriver] = useState(null);
  const [conductor, setConductor] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfileNumbers();
  }, []);

  const fetchProfileNumbers = async () => {
    try {
      const response = await axios.get('/api/profile');
      if (response.data.exists && response.data.profile) {
        const profile = response.data.profile;
        // Get latest numerology data
        const numRes = await axios.get('/api/readings/latest');
        if (numRes.data && numRes.data.reading) {
          setDriver(numRes.data.reading.driver || 5);
          setConductor(numRes.data.reading.conductor || 5);
        } else {
          setDriver(5);
          setConductor(5);
        }
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setDriver(5);
      setConductor(5);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!businessName) return;

    setLoading(true);
    try {
      const response = await axios.post('/api/numerology/business-name', {
        business_name: businessName,
        driver,
        conductor
      });
      setResult(response.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="container" style={{ maxWidth: '700px' }}>
        <button
          onClick={() => navigate('/dashboard')}
          className="btn btn-ghost"
          style={{ marginBottom: '2rem' }}
          data-testid="back-button"
        >
          <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} />
          Dashboard
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>Business Name Analysis</h1>
          <p style={{ color: '#9CA3AF', marginBottom: '2rem' }}>Check if your business name aligns with your personal numbers</p>
        </motion.div>

        <div className="card" style={{ marginBottom: '2rem' }}>
          <div className="form-group">
            <label className="form-label">Business Name</label>
            <input
              type="text"
              className="form-input"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Enter business name"
              data-testid="business-name-input"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Your Driver Number</label>
              <input
                type="number"
                className="form-input"
                min="1"
                max="8"
                value={driver}
                onChange={(e) => setDriver(parseInt(e.target.value) || 1)}
                data-testid="driver-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Your Conductor Number</label>
              <input
                type="number"
                className="form-input"
                min="1"
                max="8"
                value={conductor}
                onChange={(e) => setConductor(parseInt(e.target.value) || 1)}
                data-testid="conductor-input"
              />
            </div>
          </div>

          <button
            onClick={handleAnalyze}
            className="btn btn-primary"
            disabled={loading || !businessName}
            data-testid="analyze-button"
            style={{ width: '100%' }}
          >
            {loading ? 'Analyzing...' : 'Analyze Name'}
          </button>
        </div>

        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="card" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <Briefcase size={40} color="#10B981" style={{ margin: '0 auto 1rem' }} />
              <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>{result.business_name}</h2>
              
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
                <span className={`badge ${result.alignment === 'strong' ? 'badge-success' : 'badge-warning'}`}>
                  {result.alignment === 'strong' ? '✓ Strong Alignment' : 'Moderate Alignment'}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#00D9FF' }}>{result.name_number.conductor}</div>
                  <p style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>Business Number</p>
                </div>
                <div>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#7C3AED' }}>{result.driver}</div>
                  <p style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>Your Driver</p>
                </div>
                <div>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#10B981' }}>{result.conductor}</div>
                  <p style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>Your Conductor</p>
                </div>
              </div>

              <div className="alert alert-info" style={{ textAlign: 'left' }}>
                {result.recommendation}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default BusinessName;